/* eslint-disable camelcase */
import React from "react"
import AppTypography from "core/components/AppTypography"
import { format } from "date-fns"
import {
    getDepartmentsSelector,
    useGetDepartmentInfoQuery,
} from "store/Department/departmentSlice"
import { getStatusColor, replaceUnderscores } from "core/helpers"
import { IProductionLine } from "models/interfaces/productionLine"
import { MachineCollection } from "models/organization"
import { RootState } from "store/rootReducer"
import {
    useGetMachineCardQuery,
    useGetMachineCardStatusInfoQuery,
} from "store/Machines/machinesSlice"
import { useGetProductionLineInfoQuery } from "store/Production_lines/productionLineSlice"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { Box, Tooltip, useTheme } from "@mui/material"
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined"

import BaseCard from "../BaseCard"
import ImgWithPlaceholder from "../../ImgWithPlaceholder"
import SpecItem from "./subcomponents/SpecItem"
import { styles } from "./styles"

const MachineSpecsCard = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const machinesState = (state: RootState) => state.machines
  const siteState = (state: RootState) => state.sites

  const { selectedSiteId: siteUUID } = useSelector(siteState)
  const { machineSelected } = useSelector(machinesState)

  const { machine_id: machineId = "" } = machineSelected || {}
  const {
    imageUrl = "",
    productionLineId = "",
    machineName = "",
  } = useGetMachineCardQuery(
    {
      machineId,
    },
    {
      selectFromResult: ({ data, isFetching, isError }) => ({
        productionLineId: data?.production_line_id,
        imageUrl: data?.machine_description,
        machineName: data?.machine_name,
        // TO BE CALLED FROM INFLUX ENDPOINT
        isFetching,
        isError,
      }),
    },
  )

  const departments = useSelector((state: RootState) =>
    getDepartmentsSelector(state, siteUUID ?? ""),
  ).find(({ production_line_collection }) =>
    production_line_collection?.find(
      ({ production_line_id: id }) => id === productionLineId,
    ),
  )

  const prodId = departments?.production_line_collection?.find(
    ({ machine_collection: machines }: IProductionLine) =>
      machines?.find(
        ({ machine_id: id }: MachineCollection) => id === machineId,
      ),
  )?.production_line_id

  const deptID = departments?.department_id ?? ""

  const { departmentName = null } = useGetDepartmentInfoQuery(
    deptID,

    {
      skip: !departments?.department_id,
      selectFromResult: ({ data }) => ({
        departmentName: data?.department_name,
      }),
    },
  )

  const { productionLineName } = useGetProductionLineInfoQuery(
    prodId ?? "",

    {
      skip: !prodId,
      selectFromResult: ({ data }) => ({
        productionLineName: data?.production_line_name,
      }),
    },
  )
  const { data } = useGetMachineCardStatusInfoQuery(
    machineId,

    { skip: !machineId },
  )
  const { state: machineStatus = "" } = data || {}

  const specList = [
    { label: "machine.id.text", text: `${machineId}` },
    // { label: "machineType", text: "machine type" },
    { label: "registrationDate", text: format(new Date(), "dd MMM yyyy") },
    { label: "productionLineName.text", text: `${productionLineName}` },
    { label: "department", text: `${departmentName}` },
  ]
  return (
    <BaseCard
      sx={{
        ...styles.container,
        borderColor: getStatusColor(machineStatus, theme).main,
      }}
    >
      <Box sx={styles.machinePhotoContainer}>
        <ImgWithPlaceholder
          imgSrc={imageUrl}
          placeholderText={t("add.machine.photo")}
        />
      </Box>

      <Box sx={styles.specsContainer}>
        <Box sx={styles.headerContainer}>
          <AppTypography
            variant="h2"
            sx={styles.headerLabel}
            capitalizeFirstLetter
          >
            {replaceUnderscores(machineName)}
          </AppTypography>
          <Tooltip
            placement="left"
            arrow
            title={<AppTypography>{t("documentation.text")}</AppTypography>}
          >
            <DescriptionOutlined
              sx={{ height: "16px", width: "16px" }}
              color="primary"
            />
          </Tooltip>
        </Box>
        {specList.map((item: { label: string; text: string }) => (
          <SpecItem key={item.label} label={item.label} text={item.text} />
        ))}
      </Box>
    </BaseCard>
  )
}

export default MachineSpecsCard
